<template>
  <div
    class="dropdown"
    ref="dropdownRef"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="!openOnHover && toggleDropdown($event)"
  >
    <div style="display: flex" :class="{ open: isOpen }">
      <slot></slot>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuRef"
        class="options-wrapper"
        :class="{ mobile: isMobile || isTablet }"
        :style="dropdownStyle"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <ul :class="{ 'mobile-list': isMobile || isTablet }">
          <li
            v-for="option in filteredOptions"
            :key="option.key"
            @click="selectOption(option)"
            class="item"
            :class="{
              selected: option.key === selected,
            }"
          >
            <component v-if="option.icon" :is="option.icon" class="icon" />

            <span class="label">
              {{ option.label }}
            </span>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { useWindowSize } from "@/composables/useWindowSize"
import {
  ref,
  computed,
  defineComponent,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  type Component,
} from "vue"

export interface DropdownOption {
  key: string
  label?: string
  icon?: Component
  onClick?: (arg0: DropdownOption) => void
}

export default defineComponent({
  name: "ComponentDropdown",
  props: {
    options: {
      type: Array as () => DropdownOption[],
      required: true,
    },
    modelValue: {
      type: String,
      default: null,
    },
    open: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String as () => "right-top" | "left-bottom",
      default: "left-bottom",
    },
    openOnHover: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update:open"],
  setup(props, { emit }) {
    const isOpen = ref(false)
    const search = ref("")
    const activeIndex = ref(-1)
    const selected = ref(props.modelValue ?? null)
    const dropdownRef = ref<HTMLElement | null>(null)
    const menuRef = ref<HTMLElement | null>(null)

    const dropdownStyle = ref({
      position: "fixed",
      top: "0px",
      left: "0px",
      zIndex: "99999",
    })

    let closeTimeout: ReturnType<typeof setTimeout> | null = null

    const selectedOption = computed(() => {
      return (
        props.options.find((option) => option.key === selected.value) ||
        props.options[0]
      )
    })

    const filteredOptions = computed(() => {
      if (!props.searchable) {
        return props.options
      }

      return props.options.filter((o) =>
        o.label?.toLowerCase().includes(search.value.toLowerCase())
      )
    })

    const updatePosition = async () => {
      await nextTick()

      if (!dropdownRef.value || !menuRef.value) {
        return
      }

      const triggerRect = dropdownRef.value.getBoundingClientRect()

      const menuRect = menuRef.value.getBoundingClientRect()

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let top = triggerRect.bottom + 8
      let left = triggerRect.right - menuRect.width

      if (props.position === "right-top") {
        top = triggerRect.top - menuRect.height - 8
        left = triggerRect.left
      }

      if (
        props.position === "left-bottom" &&
        top + menuRect.height > viewportHeight
      ) {
        top = triggerRect.top - menuRect.height - 8
      }

      if (left < 8) {
        left = 8
      }

      if (left + menuRect.width > viewportWidth - 8) {
        left = viewportWidth - menuRect.width - 8
      }

      dropdownStyle.value = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        zIndex: "99999",
      }
    }

    watch(
      () => props.modelValue,
      (value) => {
        selected.value = value
      }
    )

    watch(isOpen, async (value) => {
      emit("update:open", value)

      if (value) {
        await updatePosition()
      }
    })

    function toggleDropdown(event: MouseEvent) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      isOpen.value = !isOpen.value

      if (isOpen.value) {
        activeIndex.value = 0

        nextTick(() => {
          updatePosition()
        })
      }
    }

    function selectOption(option: DropdownOption) {
      selected.value = option.key

      emit("update:modelValue", option.key)

      if (typeof option.onClick === "function") {
        option.onClick(option)
      }

      isOpen.value = false
      search.value = ""
    }

    function handleMouseEnter() {
      if (!props.openOnHover) {
        return
      }

      if (closeTimeout) {
        clearTimeout(closeTimeout)
        closeTimeout = null
      }

      isOpen.value = true

      nextTick(() => {
        updatePosition()
      })
    }

    function handleMouseLeave() {
      if (!props.openOnHover) {
        return
      }

      closeTimeout = setTimeout(() => {
        isOpen.value = false
        search.value = ""
      }, 150)
    }

    function selectActiveOption() {
      if (
        activeIndex.value >= 0 &&
        activeIndex.value < filteredOptions.value.length
      ) {
        selectOption(filteredOptions.value[activeIndex.value])
      }
    }

    function navigate(direction: number) {
      const max = filteredOptions.value.length - 1

      activeIndex.value = Math.min(
        Math.max(activeIndex.value + direction, 0),
        max
      )
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node

      const clickedTrigger = dropdownRef.value?.contains(target)

      const clickedMenu = menuRef.value?.contains(target)

      if (!clickedTrigger && !clickedMenu) {
        isOpen.value = false
      }
    }

    function handleViewportChange() {
      if (!isOpen.value) {
        return
      }

      updatePosition()
    }

    onMounted(() => {
      document.addEventListener("click", handleClickOutside)

      window.addEventListener("resize", handleViewportChange)

      window.addEventListener("scroll", handleViewportChange, true)
    })

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside)

      window.removeEventListener("resize", handleViewportChange)

      window.removeEventListener("scroll", handleViewportChange, true)

      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
    })

    const { isMobile, isTablet } = useWindowSize()

    return {
      isOpen,
      search,
      activeIndex,
      selected,
      selectedOption,
      filteredOptions,
      dropdownRef,
      menuRef,
      dropdownStyle,
      isMobile,
      isTablet,
      toggleDropdown,
      selectOption,
      selectActiveOption,
      navigate,
      handleMouseEnter,
      handleMouseLeave,
    }
  },
})
</script>

<style scoped lang="scss">
.dropdown {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.options-wrapper {
  position: fixed;
  z-index: 99999;

  background: var(--black-gradient);
  box-shadow: var(--black-gradient);
  border-radius: 24px;

  will-change: transform;

  &.mobile {
    border-radius: 12px;
  }

  ul {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    gap: 10px;
    margin: 0;
    list-style: none;

    &.mobile-list {
      padding: 12px;
    }
  }

  .item {
    cursor: pointer;
    width: 124px;

    display: flex;
    align-items: center;
    gap: 8px;

    height: 20px;
    opacity: 0.8;

    .label {
      font-family: "Montserrat";
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
      letter-spacing: -0.4px;
      color: rgba(255, 255, 255, 0.6);
      white-space: nowrap;
    }

    .icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    &.selected,
    &:hover {
      .label {
        color: #ffffff;
      }
    }
  }
}
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}
</style>
